import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { ArrowLeft, Check, ChevronRight, Copy, KeyRound, Loader2, Mail } from "lucide-react";

import { NumberField, TextField } from "@/components/admin/catalogo/fields";
import { CommissionsCard } from "@/components/admin/equipe/CommissionsCard";
import { PreviewBadge } from "@/components/admin/config/MercadoPagoSettings";
import { AdminCard, ListState } from "@/components/admin/primitives";
import { PasswordChecklist, isStrongPassword } from "@/components/PasswordChecklist";
import { formatDate, formatDateTime, relativeDays } from "@/lib/dates";
import { errorMessage } from "@/lib/errors";
import { formatBRL } from "@/lib/format";
import {
  referralUrl,
  useMemberDetail,
  useSendInvite,
  useSetMemberPassword,
  useUpdateReseller,
  type MemberDetail,
  type ResellerAdminDetail,
} from "@/lib/resellers";

const ROLE_LABEL = { ADMIN: "Administrador", ATENDENTE: "Atendente", REVENDEDOR: "Revendedor" };
const STATUS_LABEL = {
  ATIVO: "Ativo",
  CONVITE_PENDENTE: "Convite pendente",
  DESATIVADO: "Acesso desativado",
};

/**
 * Uma pessoa da equipe.
 *
 * Para revendedor, é aqui que o admin define a comissão (geral ou por
 * categoria), os dados de repasse e acompanha a carteira. O contato dos
 * clientes aparece completo — é a empresa olhando os próprios clientes.
 */
export const Route = createFileRoute("/admin/_painel/equipe/$membroId")({
  head: () => ({ meta: [{ title: "Equipe — Painel MonteBox" }] }),
  component: MemberPage,
});

function MemberPage() {
  const { membroId } = Route.useParams();
  const detail = useMemberDetail(membroId);
  const data = detail.data;

  if (!data) {
    return (
      <AdminCard className="m-8">
        <ListState
          loading={detail.isLoading}
          error={detail.error}
          empty={!detail.isLoading}
          emptyLabel="Pessoa não encontrada."
        />
      </AdminCard>
    );
  }

  const { member, reseller } = data;

  return (
    <>
      <header className="flex min-h-16 shrink-0 flex-wrap items-center gap-3 border-b border-mb-line bg-white px-4 py-3 sm:px-8">
        <Link
          to="/admin/equipe"
          aria-label="Voltar para a equipe"
          className="grid h-9 w-9 place-items-center rounded-full border border-mb-line text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
        >
          <ArrowLeft className="h-[18px] w-[18px]" />
        </Link>
        <Link to="/admin/equipe" className="text-sm font-semibold text-mb-muted">
          Equipe
        </Link>
        <ChevronRight className="h-4 w-4 text-mb-muted" />
        <h1 className="font-display text-xl font-bold tracking-tight text-mb-navy">
          {member.name}
        </h1>
        <span className="rounded-full bg-mb-surface px-3 py-1 text-xs font-bold text-mb-muted">
          {ROLE_LABEL[member.role]}
        </span>
      </header>

      <div className="grid items-start gap-6 p-4 sm:p-8 xl:grid-cols-[1fr_380px]">
        <div className="flex flex-col gap-6">
          {reseller ? (
            <>
              <ResellerNumbers reseller={reseller} />
              <Commission memberId={member.id} reseller={reseller} />
              <CommissionsCard reseller={reseller} />
              <Portfolio reseller={reseller} />
              <RateHistory reseller={reseller} />
            </>
          ) : (
            <AdminCard className="p-6 text-sm text-mb-muted">
              {ROLE_LABEL[member.role]} trabalha no painel da equipe. Papel e acesso se mudam na
              lista da Equipe.
            </AdminCard>
          )}
        </div>

        <div className="flex flex-col gap-6">
          <Access data={data} />
          {reseller && <ResellerLink reseller={reseller} />}
          {reseller && <Payout memberId={member.id} reseller={reseller} />}
        </div>
      </div>
    </>
  );
}

function Access({ data }: { data: MemberDetail }) {
  const { member } = data;
  const setPassword = useSetMemberPassword();
  const sendInvite = useSendInvite();
  const [password, setPasswordValue] = useState("");

  return (
    <AdminCard className="p-6">
      <h2 className="font-display text-base font-bold text-mb-navy">Acesso</h2>
      <dl className="mt-4 grid grid-cols-[110px_1fr] gap-y-2 text-sm">
        <dt className="text-mb-muted">E-mail</dt>
        <dd className="truncate text-mb-ink">{member.email}</dd>
        <dt className="text-mb-muted">Situação</dt>
        <dd className="text-mb-ink">{STATUS_LABEL[member.status]}</dd>
        <dt className="text-mb-muted">Último acesso</dt>
        <dd
          className="text-mb-ink"
          title={member.lastLoginAt ? formatDateTime(member.lastLoginAt) : undefined}
        >
          {member.lastLoginAt ? relativeDays(member.lastLoginAt) : "Nunca acessou"}
        </dd>
        <dt className="text-mb-muted">Na equipe desde</dt>
        <dd className="text-mb-ink">{formatDate(member.createdAt)}</dd>
      </dl>

      {!member.isMe && member.status !== "DESATIVADO" && (
        <div className="mt-5 border-t border-mb-line pt-5">
          <p className="flex items-center gap-1.5 text-xs font-bold text-mb-ink">
            <Mail className="h-3.5 w-3.5" />
            {member.status === "CONVITE_PENDENTE" ? "Reenviar convite" : "Enviar link de senha"}
          </p>
          <p className="mt-1 text-[11px] leading-4 text-mb-muted">
            Manda para {member.email} um link para a pessoa criar a própria senha (vale 7 dias).
          </p>
          <button
            type="button"
            disabled={sendInvite.isPending}
            onClick={() =>
              sendInvite.mutate(member.id, {
                onSuccess: ({ sent }) =>
                  sent
                    ? toast.success(`Link enviado para ${member.email}.`)
                    : toast.error("O e-mail não saiu. Confira o SMTP ou defina a senha abaixo."),
                onError: (cause) => toast.error(errorMessage(cause)),
              })
            }
            className="mt-2 flex h-9 items-center gap-2 rounded-mb-button border border-mb-line px-4 text-xs font-bold text-mb-navy transition-colors hover:border-mb-navy disabled:opacity-50"
          >
            {sendInvite.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Mail className="h-3.5 w-3.5" />
            )}
            Enviar por e-mail
          </button>
        </div>
      )}

      {!member.isMe && member.status !== "DESATIVADO" && (
        <div className="mt-5 border-t border-mb-line pt-5">
          <p className="flex items-center gap-1.5 text-xs font-bold text-mb-ink">
            <KeyRound className="h-3.5 w-3.5" />
            Definir senha à mão
          </p>
          <p className="mt-1 text-[11px] leading-4 text-mb-muted">
            Se o e-mail não chegar, defina aqui e repasse à pessoa por um canal seguro — ela troca
            depois em "Meus dados". Sessões abertas caem.
          </p>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={password}
              autoComplete="new-password"
              placeholder="senha forte"
              onChange={(event) => setPasswordValue(event.target.value)}
              className="h-9 min-w-0 grow rounded-mb-field border border-mb-line px-3 text-sm outline-none focus:border-mb-navy"
            />
            <button
              type="button"
              disabled={
                setPassword.isPending ||
                !isStrongPassword(password, { name: member.name, email: member.email })
              }
              onClick={() =>
                setPassword.mutate(
                  { memberId: member.id, password },
                  {
                    onSuccess: () => {
                      toast.success("Senha definida. Repasse à pessoa por um canal seguro.");
                      setPasswordValue("");
                    },
                    onError: (cause) => toast.error(errorMessage(cause)),
                  },
                )
              }
              className="h-9 shrink-0 rounded-mb-button bg-mb-navy px-4 text-xs font-bold text-white disabled:opacity-50"
            >
              Definir
            </button>
          </div>
          <PasswordChecklist
            password={password}
            name={member.name}
            email={member.email}
            className="mt-2 text-mb-muted"
          />
        </div>
      )}
    </AdminCard>
  );
}

function ResellerLink({ reseller }: { reseller: ResellerAdminDetail }) {
  return (
    <AdminCard className="p-6">
      <h2 className="font-display text-base font-bold text-mb-navy">Link de indicação</h2>
      {reseller.code ? (
        <>
          <div className="mt-3 flex items-center gap-2 rounded-mb-field bg-mb-surface px-3 py-2">
            <span className="min-w-0 grow truncate font-mono text-xs text-mb-ink">
              {referralUrl(reseller.code)}
            </span>
            <button
              type="button"
              aria-label="Copiar link"
              onClick={() => {
                void navigator.clipboard.writeText(referralUrl(reseller.code!));
                toast.success("Link copiado.");
              }}
              className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-mb-navy hover:bg-white"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="mt-2 text-[11px] text-mb-muted">
            Criado em {formatDate(reseller.codeCreatedAt!)}. Não pode ser alterado.
          </p>
        </>
      ) : (
        <p className="mt-3 text-sm text-mb-muted">
          O revendedor ainda não criou o link. É ele quem escolhe, uma vez, no painel dele.
        </p>
      )}
    </AdminCard>
  );
}

function ResellerNumbers({ reseller }: { reseller: ResellerAdminDetail }) {
  const { stats } = reseller;
  const tiles = [
    { label: "Clientes na carteira", value: String(stats.customers) },
    { label: "Vendas creditadas", value: String(stats.sales) },
    { label: "Faturamento creditado", value: formatBRL(stats.revenue) },
    { label: "Este mês", value: `${formatBRL(stats.revenueThisMonth)} · ${stats.salesThisMonth}` },
    { label: "Pedidos em aberto", value: String(stats.openOrders) },
  ];
  return (
    <div className="grid grid-cols-5 gap-4">
      {tiles.map((tile) => (
        <AdminCard key={tile.label} className="p-4">
          <p className="text-[11px] font-bold text-mb-muted">{tile.label}</p>
          <p className="mt-1 font-display text-lg font-bold text-mb-navy">{tile.value}</p>
        </AdminCard>
      ))}
    </div>
  );
}

/**
 * Comissão: taxa geral e, se precisar, taxa própria por categoria.
 *
 * Campo de categoria em branco = vale a geral. Tudo grava junto, e cada
 * mudança entra no histórico. Mudar a taxa não mexe em comissão já gerada.
 */
function Commission({ memberId, reseller }: { memberId: string; reseller: ResellerAdminDetail }) {
  const update = useUpdateReseller();
  const [defaultRate, setDefaultRate] = useState<number | null>(reseller.defaultRate);
  const [rates, setRates] = useState<Record<string, number | null>>({});

  useEffect(() => {
    setDefaultRate(reseller.defaultRate);
    setRates(Object.fromEntries(reseller.categoryRates.map((row) => [row.categoryId, row.rate])));
  }, [reseller]);

  const save = () =>
    update.mutate(
      {
        memberId,
        defaultRate: defaultRate ?? 0,
        categoryRates: reseller.categoryRates.map((row) => ({
          categoryId: row.categoryId,
          rate: rates[row.categoryId] ?? null,
        })),
      },
      {
        onSuccess: () => toast.success("Comissão atualizada."),
        onError: (cause) => toast.error(errorMessage(cause)),
      },
    );

  return (
    <AdminCard className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-base font-bold text-mb-navy">Comissão</h2>
          <p className="mt-1 max-w-xl text-xs text-mb-muted">
            Percentual sobre o valor das peças (sem frete), em toda venda paga de cliente da
            carteira — inclusive recompras. Mudar a taxa não altera comissões já calculadas.
          </p>
        </div>
        <button
          type="button"
          disabled={update.isPending}
          onClick={save}
          className="flex shrink-0 items-center gap-2 rounded-mb-button bg-mb-yellow px-5 py-2.5 font-display text-sm font-bold text-mb-ink shadow-cta disabled:opacity-50"
        >
          {update.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4 stroke-[3]" />
          )}
          Salvar comissão
        </button>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
        <NumberField
          label="Taxa geral (%)"
          value={defaultRate}
          onChange={setDefaultRate}
          hint="Vale para categoria sem taxa própria."
        />
        {reseller.categoryRates.map((row) => (
          <NumberField
            key={row.categoryId}
            label={`${row.label} (%)`}
            value={rates[row.categoryId] ?? null}
            onChange={(value) => setRates((current) => ({ ...current, [row.categoryId]: value }))}
            placeholder={`geral: ${String(defaultRate ?? 0).replace(".", ",")}`}
          />
        ))}
      </div>
    </AdminCard>
  );
}

function Payout({ memberId, reseller }: { memberId: string; reseller: ResellerAdminDetail }) {
  const update = useUpdateReseller();
  const [storeName, setStoreName] = useState(reseller.storeName ?? "");
  const [pixKey, setPixKey] = useState(reseller.pixKey ?? "");
  const [holderName, setHolderName] = useState(reseller.holderName ?? "");
  const [holderDoc, setHolderDoc] = useState(reseller.holderDoc ?? "");

  return (
    <AdminCard className="p-6">
      <h2 className="font-display text-base font-bold text-mb-navy">Repasse e exibição</h2>
      <div className="mt-3 flex items-center justify-between gap-3 rounded-mb-field bg-mb-surface px-3 py-2.5">
        <span className="flex items-center gap-2 text-xs font-bold text-mb-ink">
          Conta Mercado Pago
          <PreviewBadge />
        </span>
        <span
          className={
            reseller.mercadoPagoConnected
              ? "text-xs font-bold text-emerald-700"
              : "text-xs font-bold text-mb-muted"
          }
        >
          {reseller.mercadoPagoConnected ? "Conectada" : "Não conectada"}
        </span>
      </div>
      <div className="mt-4 flex flex-col gap-3">
        <TextField
          label="Nome na loja"
          value={storeName}
          onChange={setStoreName}
          placeholder="Ex.: João Uniformes"
          hint="Aparece para quem chega pelo link. Em branco: o primeiro nome."
        />
        <TextField label="Chave Pix" value={pixKey} onChange={setPixKey} />
        <TextField label="Titular" value={holderName} onChange={setHolderName} />
        <TextField
          label="CPF/CNPJ do titular"
          value={holderDoc}
          onChange={setHolderDoc}
          hint="Também impede autoindicação: compra com este documento não gera comissão."
        />
      </div>
      <button
        type="button"
        disabled={update.isPending}
        onClick={() =>
          update.mutate(
            { memberId, storeName, pixKey, holderName, holderDoc },
            {
              onSuccess: () => toast.success("Dados de repasse salvos."),
              onError: (cause) => toast.error(errorMessage(cause)),
            },
          )
        }
        className="mt-4 w-full rounded-mb-button bg-mb-navy px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
      >
        Salvar
      </button>
    </AdminCard>
  );
}

function Portfolio({ reseller }: { reseller: ResellerAdminDetail }) {
  return (
    <AdminCard className="p-6">
      <h2 className="font-display text-base font-bold text-mb-navy">
        Carteira · {reseller.customers.length}
      </h2>
      <p className="mt-1 text-xs text-mb-muted">
        Clientes com vínculo ativo. O cliente é da empresa — para vincular, transferir ou
        desvincular, abra a ficha do cliente.
      </p>
      {reseller.customers.length === 0 ? (
        <p className="py-6 text-center text-sm text-mb-muted">Nenhum cliente vinculado ainda.</p>
      ) : (
        <table className="mt-4 w-full text-sm">
          <thead className="text-left text-[11px] font-bold uppercase tracking-[0.08em] text-mb-muted">
            <tr>
              <th className="pb-2">Cliente</th>
              <th className="pb-2">Vínculo</th>
              <th className="pb-2 text-right">Vendas</th>
              <th className="pb-2 text-right">Faturamento</th>
              <th className="pb-2 text-right">Última compra</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {reseller.customers.map((customer) => (
              <tr key={customer.id} className="border-t border-mb-line">
                <td className="py-2.5">
                  <Link
                    to="/admin/clientes/$clienteId"
                    params={{ clienteId: customer.key }}
                    className="font-semibold text-mb-ink hover:text-mb-navy"
                  >
                    {customer.name}
                  </Link>
                  <span className="ml-2 text-xs text-mb-muted">{customer.phone}</span>
                </td>
                <td className="py-2.5 text-xs text-mb-muted">
                  {customer.source === "MANUAL" ? "Manual" : "Pelo link"} ·{" "}
                  {formatDate(customer.linkedAt)}
                </td>
                <td className="py-2.5 text-right text-mb-muted">{customer.sales}</td>
                <td className="py-2.5 text-right text-mb-ink">{formatBRL(customer.revenue)}</td>
                <td className="py-2.5 text-right text-mb-muted">
                  {customer.lastSaleAt ? formatDate(customer.lastSaleAt) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </AdminCard>
  );
}

function RateHistory({ reseller }: { reseller: ResellerAdminDetail }) {
  if (reseller.rateHistory.length === 0) return null;
  const rate = (value: number | null) =>
    value === null ? "geral" : `${String(value).replace(".", ",")}%`;
  return (
    <AdminCard className="p-6">
      <h2 className="font-display text-base font-bold text-mb-navy">Histórico de taxas</h2>
      <ul className="mt-3 flex flex-col gap-1.5 text-sm">
        {reseller.rateHistory.map((row) => (
          <li key={row.id} className="flex items-baseline justify-between gap-3">
            <span className="text-mb-ink">
              {row.category ?? "Taxa geral"}: {rate(row.fromRate)} → {rate(row.toRate)}
            </span>
            <span className="shrink-0 text-xs text-mb-muted">
              {row.changedBy} · {formatDateTime(row.createdAt)}
            </span>
          </li>
        ))}
      </ul>
    </AdminCard>
  );
}
